import type { CapRef } from "@gelatos/protocol";
import * as Y from "yjs";

/**
 * The workspace document (§6.1).
 *
 * ```
 * workspace
 *   rooms   : Y.Map<RoomId, Room>
 *   objects : Y.Map<ObjectId, ObjectMeta>   // index only, never content
 * ```
 *
 * `rooms` and `objects` are Yjs **root types**, not entries nested inside a
 * `workspace` map. That is a correctness requirement, not a style choice: a
 * container created lazily by whichever device touches it first means two
 * devices each create their own, and a `Y.Map` key is last-writer-wins, so one
 * device's entire subtree is discarded when they merge. Root types are the one
 * kind of container every peer agrees on without anyone having to write it.
 *
 * The same rule applies further down, which is why nothing here creates a
 * shared container from a read path. A room and its `windows` array are created
 * together in one transaction; a window and its `geometry` map likewise. Only
 * one device ever creates a given room or window, so those have no concurrent
 * creation to lose.
 *
 * Every read produces a plain snapshot and every write goes through a function
 * in this file, so the Yjs types stay behind one boundary. The shell never sees
 * a `Y.Map`.
 */

export type RoomId = string;
export type ObjectId = string;
export type WindowId = string;
export type Layout = "floating" | "tiles";

export type Geometry = { x: number; y: number; w: number; h: number };

export type WindowSnapshot = {
  id: WindowId;
  appId: string;
  title: string;
  geometry: Geometry;
  tileIndex: number;
  caps: CapRef[];
};

export type RoomSnapshot = {
  id: RoomId;
  name: string;
  layout: Layout;
  created: number;
  windows: WindowSnapshot[];
};

export type ObjectSnapshot = {
  id: ObjectId;
  type: string;
  name: string;
  created: number;
  attrs: Record<string, string | number | boolean>;
};

export type WorkspaceSnapshot = {
  rooms: RoomSnapshot[];
  objects: ObjectSnapshot[];
};

export const MIN_SIZE = { w: 240, h: 150 } as const;

const DEFAULT_GEOMETRY: Geometry = { x: 64, y: 56, w: 300, h: 190 };

/* ------------------------------------------------------------ Yjs helpers */

type AnyMap = Y.Map<unknown>;
type AnyArray = Y.Array<unknown>;

/** The document's root containers. Held together so callers pass one thing. */
export type WorkspaceDoc = {
  doc: Y.Doc;
  rooms: AnyMap;
  objects: AnyMap;
  /**
   * The workspace's environment, as a value (§4.6.4).
   *
   * A Nix flake, living in the replicated document like everything else. This
   * is what "environment-as-value" means concretely: the environment is not
   * configuration attached to a machine, it is a string in the workspace, and
   * the Sprite is a *materialisation* of it that can be thrown away and rebuilt.
   * Edit the flake on the laptop and the phone has the same environment before
   * either has a machine to run it on.
   */
  env: AnyMap;
};

export function workspaceRoot(doc: Y.Doc): WorkspaceDoc {
  return {
    doc,
    rooms: doc.getMap<unknown>("rooms"),
    objects: doc.getMap<unknown>("objects"),
    env: doc.getMap<unknown>("env"),
  };
}

/**
 * The default environment for a new workspace.
 *
 * Deliberately small. A flake that takes four minutes to realize would make
 * every measurement in §4.6.6 a measurement of this file instead.
 */
export const DEFAULT_FLAKE = `{
  description = "a gelatOS workspace";

  inputs.nixpkgs.url = "github:NixOS/nixpkgs/nixos-24.05";

  outputs = { self, nixpkgs }:
    let
      system = "x86_64-linux";
      pkgs = import nixpkgs { inherit system; };
    in {
      devShells.\${system}.default = pkgs.mkShell {
        packages = with pkgs; [ ripgrep jq git ];
      };
    };
}
`;

/** The flake this workspace's Sprites are built from. */
export function flake(ws: WorkspaceDoc): string {
  const value = ws.env.get("flake");
  return typeof value === "string" && value !== "" ? value : DEFAULT_FLAKE;
}

export function setFlake(ws: WorkspaceDoc, source: string): void {
  ws.doc.transact(() => {
    ws.env.set("flake", source);
    ws.env.set("flakeUpdated", Date.now());
  });
}

function asMap(value: unknown): AnyMap | undefined {
  return value instanceof Y.Map ? (value as AnyMap) : undefined;
}

function asArray(value: unknown): AnyArray | undefined {
  return value instanceof Y.Array ? (value as AnyArray) : undefined;
}

/** Read a nested container. Never creates one — see the note at the top. */
function childMap(parent: AnyMap, key: string): AnyMap | undefined {
  return asMap(parent.get(key));
}

function childArray(parent: AnyMap, key: string): AnyArray | undefined {
  return asArray(parent.get(key));
}

function readString(map: AnyMap, key: string, fallback: string): string {
  const value = map.get(key);
  return typeof value === "string" ? value : fallback;
}

function readNumber(map: AnyMap, key: string, fallback: number): number {
  const value = map.get(key);
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/* --------------------------------------------------------------- snapshot */

function snapshotGeometry(map: AnyMap): Geometry {
  const geometry = childMap(map, "geometry");
  if (geometry === undefined) return { ...DEFAULT_GEOMETRY };
  return {
    x: readNumber(geometry, "x", DEFAULT_GEOMETRY.x),
    y: readNumber(geometry, "y", DEFAULT_GEOMETRY.y),
    w: Math.max(MIN_SIZE.w, readNumber(geometry, "w", DEFAULT_GEOMETRY.w)),
    h: Math.max(MIN_SIZE.h, readNumber(geometry, "h", DEFAULT_GEOMETRY.h)),
  };
}

function snapshotCaps(map: AnyMap): CapRef[] {
  const caps = childArray(map, "caps");
  if (caps === undefined) return [];
  const out: CapRef[] = [];
  for (const entry of caps.toArray()) {
    const cap = asMap(entry);
    if (cap === undefined) continue;
    const id = readString(cap, "id", "");
    const kind = readString(cap, "kind", "");
    const target = readString(cap, "target", "");
    if (id === "" || target === "") continue;
    if (kind !== "object" && kind !== "collection" && kind !== "stream" && kind !== "exec") {
      continue;
    }
    const ref: CapRef = { id, kind, target };
    const objectType = cap.get("objectType");
    if (typeof objectType === "string") ref.objectType = objectType;
    const label = cap.get("label");
    if (typeof label === "string") ref.label = label;
    out.push(ref);
  }
  return out;
}

function snapshotWindow(map: AnyMap): WindowSnapshot | null {
  const id = readString(map, "id", "");
  const appId = readString(map, "appId", "");
  if (id === "" || appId === "") return null;
  return {
    id,
    appId,
    title: readString(map, "title", appId),
    geometry: snapshotGeometry(map),
    tileIndex: readNumber(map, "tileIndex", 0),
    caps: snapshotCaps(map),
  };
}

function snapshotRoom(id: RoomId, map: AnyMap): RoomSnapshot {
  const layout = readString(map, "layout", "floating");
  const list = childArray(map, "windows");
  const windows: WindowSnapshot[] = [];
  if (list !== undefined) {
    for (const entry of list.toArray()) {
      const windowMap = asMap(entry);
      if (windowMap === undefined) continue;
      const snapshot = snapshotWindow(windowMap);
      if (snapshot !== null) windows.push(snapshot);
    }
  }
  return {
    id,
    name: readString(map, "name", "Room"),
    layout: layout === "tiles" ? "tiles" : "floating",
    created: readNumber(map, "created", 0),
    windows,
  };
}

function snapshotObject(id: ObjectId, map: AnyMap): ObjectSnapshot {
  const attrsMap = childMap(map, "attrs");
  const attrs: Record<string, string | number | boolean> = {};
  if (attrsMap !== undefined) {
    for (const [key, value] of attrsMap.entries()) {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        attrs[key] = value;
      }
    }
  }
  return {
    id,
    type: readString(map, "type", "application/octet-stream"),
    name: readString(map, "name", "Untitled"),
    created: readNumber(map, "created", 0),
    attrs,
  };
}

export function snapshot(ws: WorkspaceDoc): WorkspaceSnapshot {
  const roomList: RoomSnapshot[] = [];
  for (const [id, value] of ws.rooms.entries()) {
    const map = asMap(value);
    if (map !== undefined) roomList.push(snapshotRoom(id, map));
  }
  // Y.Map is unordered; rooms carry a creation stamp so the switcher is stable.
  roomList.sort((a, b) => a.created - b.created || a.id.localeCompare(b.id));

  const objectList: ObjectSnapshot[] = [];
  for (const [id, value] of ws.objects.entries()) {
    const map = asMap(value);
    if (map !== undefined) objectList.push(snapshotObject(id, map));
  }
  objectList.sort((a, b) => a.created - b.created || a.id.localeCompare(b.id));

  return { rooms: roomList, objects: objectList };
}

/* -------------------------------------------------------------- mutations */

function roomMap(ws: WorkspaceDoc, id: RoomId): AnyMap | undefined {
  return asMap(ws.rooms.get(id));
}

function windowMap(ws: WorkspaceDoc, roomId: RoomId, windowId: WindowId): AnyMap | undefined {
  const room = roomMap(ws, roomId);
  if (room === undefined) return undefined;
  const list = childArray(room, "windows");
  if (list === undefined) return undefined;
  for (const entry of list.toArray()) {
    const map = asMap(entry);
    if (map !== undefined && map.get("id") === windowId) return map;
  }
  return undefined;
}

export function createRoom(ws: WorkspaceDoc, name: string, now: number): RoomId {
  const id = crypto.randomUUID();
  ws.doc.transact(() => {
    const room: AnyMap = new Y.Map();
    ws.rooms.set(id, room);
    room.set("name", name);
    room.set("layout", "floating");
    room.set("created", now);
    // Created here and only here, in the same transaction as the room itself.
    room.set("windows", new Y.Array());
  });
  return id;
}

export function renameRoom(ws: WorkspaceDoc, id: RoomId, name: string): void {
  ws.doc.transact(() => {
    roomMap(ws, id)?.set("name", name);
  });
}

export function deleteRoom(ws: WorkspaceDoc, id: RoomId): void {
  ws.doc.transact(() => {
    ws.rooms.delete(id);
  });
}

export function setLayout(ws: WorkspaceDoc, id: RoomId, layout: Layout): void {
  ws.doc.transact(() => {
    roomMap(ws, id)?.set("layout", layout);
  });
}

export function openWindow(
  ws: WorkspaceDoc,
  roomId: RoomId,
  spec: { appId: string; title: string; size: { w: number; h: number } },
): WindowId | null {
  const room = roomMap(ws, roomId);
  const list = room === undefined ? undefined : childArray(room, "windows");
  if (list === undefined) return null;

  const id = crypto.randomUUID();
  ws.doc.transact(() => {
    const step = list.length % 8;
    const record: AnyMap = new Y.Map();
    list.push([record]);
    record.set("id", id);
    record.set("appId", spec.appId);
    record.set("title", spec.title);
    record.set("tileIndex", list.length - 1);
    record.set("caps", new Y.Array());
    const geometry: AnyMap = new Y.Map();
    record.set("geometry", geometry);
    geometry.set("x", DEFAULT_GEOMETRY.x + step * 28);
    geometry.set("y", DEFAULT_GEOMETRY.y + step * 28);
    geometry.set("w", spec.size.w);
    geometry.set("h", spec.size.h);
  });
  return id;
}

export function closeWindow(ws: WorkspaceDoc, roomId: RoomId, windowId: WindowId): void {
  const room = roomMap(ws, roomId);
  const list = room === undefined ? undefined : childArray(room, "windows");
  if (list === undefined) return;
  ws.doc.transact(() => {
    const index = list.toArray().findIndex((entry) => asMap(entry)?.get("id") === windowId);
    if (index >= 0) list.delete(index, 1);
  });
}

export function setGeometry(
  ws: WorkspaceDoc,
  roomId: RoomId,
  windowId: WindowId,
  next: Geometry,
): void {
  const record = windowMap(ws, roomId, windowId);
  const geometry = record === undefined ? undefined : childMap(record, "geometry");
  if (geometry === undefined) return;
  ws.doc.transact(() => {
    // Field-level writes: two devices moving different windows — or the same
    // window on different axes — merge instead of clobbering.
    geometry.set("x", Math.round(next.x));
    geometry.set("y", Math.round(next.y));
    geometry.set("w", Math.round(Math.max(MIN_SIZE.w, next.w)));
    geometry.set("h", Math.round(Math.max(MIN_SIZE.h, next.h)));
  });
}

export function setTitle(
  ws: WorkspaceDoc,
  roomId: RoomId,
  windowId: WindowId,
  title: string,
): void {
  const record = windowMap(ws, roomId, windowId);
  if (record === undefined || record.get("title") === title) return;
  ws.doc.transact(() => {
    record.set("title", title);
  });
}

/** Swap two windows' places in the tile grid. */
export function swapTiles(ws: WorkspaceDoc, roomId: RoomId, a: WindowId, b: WindowId): void {
  if (a === b) return;
  const first = windowMap(ws, roomId, a);
  const second = windowMap(ws, roomId, b);
  if (first === undefined || second === undefined) return;
  ws.doc.transact(() => {
    const firstIndex = readNumber(first, "tileIndex", 0);
    const secondIndex = readNumber(second, "tileIndex", 0);
    first.set("tileIndex", secondIndex);
    second.set("tileIndex", firstIndex);
  });
}

/* ---------------------------------------------------------------- objects */

/**
 * Create an object in the index (§6.1). Content lives in its own document
 * (§6.2) and is never put here — this map has to stay small enough to load
 * eagerly on every device.
 */
export function createObject(
  ws: WorkspaceDoc,
  spec: { type: string; name: string; owner: string; now: number },
): ObjectId {
  const id = crypto.randomUUID();
  ws.doc.transact(() => {
    const meta: AnyMap = new Y.Map();
    ws.objects.set(id, meta);
    meta.set("type", spec.type);
    meta.set("name", spec.name);
    meta.set("created", spec.now);
    // Created with the object, never lazily — see the note at the top.
    const attrs: AnyMap = new Y.Map();
    meta.set("attrs", attrs);
    // Which app's private store this belongs to. The powerbox can hand any
    // object to any app; this only decides what an app sees *without* asking.
    attrs.set("owner", spec.owner);
  });
  return id;
}

export function objectMeta(ws: WorkspaceDoc, id: ObjectId): ObjectSnapshot | null {
  const meta = asMap(ws.objects.get(id));
  return meta === undefined ? null : snapshotObject(id, meta);
}

export function renameObject(ws: WorkspaceDoc, id: ObjectId, name: string): void {
  ws.doc.transact(() => {
    asMap(ws.objects.get(id))?.set("name", name);
  });
}

/** True when this workspace has never been used by anyone. */
export function isEmpty(ws: WorkspaceDoc): boolean {
  return ws.rooms.size === 0;
}

/** Create the first room the first time a workspace is opened. */
export function ensureRoom(ws: WorkspaceDoc, now: number): void {
  if (!isEmpty(ws)) return;
  createRoom(ws, "Home", now);
}
